import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import axios from "./axios";

/**
 * Ce que l'invite doit faire ensuite, decide PAR LE SERVEUR.
 *
 * Cet ecran choisissait lui-meme, a partir de `user_exists` -- un booleen qui
 * valait `is_active = 1` sur la table `user`. Or "le compte n'est pas
 * desactive" n'est pas "cette personne connait un mot de passe". Des que les
 * deux divergeaient (compte existant, compte active par un rattachement admin,
 * pre-compte d'invitation), l'ecran affichait "vous avez deja un compte,
 * connectez-vous" a quelqu'un qui n'avait aucun identifiant, sans meme lui
 * proposer "mot de passe oublie". Impasse totale : l'invitation expirait au
 * bout de 72 h et l'administrateur voyait "Nombre d'utilisateurs : 0".
 *
 *   set_password      aucun mot de passe connu : il en cree un ici
 *   sign_in           le compte existe deja avec un mot de passe : il
 *                     s'authentifie, le rattachement se fait a la connexion
 *   already_member    deja rattache a cette societe
 *   already_accepted  invitation deja consommee
 *   expired / revoked etats terminaux
 */
export type TInvitationNextAction =
  | "set_password"
  | "sign_in"
  | "already_member"
  | "already_accepted"
  | "expired"
  | "revoked";

export type TInvitation = {
  company_id: number;
  company_name: string;
  email: string;
  role: "FLEET_MANAGER" | "EMPLOYEE" | "SUPER_ADMIN";
  firstname: string | null;
  lastname: string | null;
  status: "pending" | "accepted" | "revoked" | "expired";
  expires_at: string | null;
  /** Voir TInvitationNextAction. Absent si l'API est plus ancienne que ce client. */
  next_action?: TInvitationNextAction;
  /** Le proprietaire du compte a deja choisi un mot de passe. */
  has_password?: boolean;
  /**
   * DEPRECIE. Conserve uniquement pour le repli ci-dessous quand ce client est
   * deploye devant une API anterieure a `next_action`. Ne jamais s'en servir
   * pour choisir un ecran : c'est exactement ce qui a produit l'impasse.
   */
  user_exists: boolean;
};

/**
 * Repli pour une API anterieure. Il reproduit l'ancien comportement, defaut
 * compris -- c'est assume : mieux vaut l'ancien ecran imparfait qu'une page
 * blanche, et il disparait des que les deux sont deployes ensemble.
 */
export const resolveNextAction = (
  invitation?: TInvitation
): TInvitationNextAction | undefined => {
  if (!invitation) return undefined;
  if (invitation.next_action) return invitation.next_action;

  if (invitation.status === "expired") return "expired";
  if (invitation.status === "revoked") return "revoked";
  if (invitation.status === "accepted") return "already_accepted";

  return invitation.user_exists ? "sign_in" : "set_password";
};

/**
 * An invitation row as returned by GET /companies/{id}/employees/invitations.
 * Richer than the public lookup above: includes the code (needed to resend or
 * revoke), the audit timestamps and the pre-filled department/site.
 */
export type TCompanyInvitation = {
  id: number;
  invitation_code: string;
  company_id: number;
  company_email: string;
  company_role: "FLEET_MANAGER" | "EMPLOYEE" | "SUPER_ADMIN";
  status: "pending" | "accepted" | "revoked" | "expired";
  expires_at: string | null;
  accepted_at: string | null;
  invited_by: number | null;
  firstname: string | null;
  lastname: string | null;
  department_id: number | null;
  site_id: number | null;
  created_at: string | null;
  updated_at: string | null;
};

export const invitationKeys = {
  byCode: (code: string | null) => ["invitation", "code", code] as const,
  companyList: (companyId: number) =>
    ["invitations", "company", companyId] as const,
};

/**
 * Public lookup of an invitation by its code (from the email link).
 * Used by the /invitation landing page to greet the invitee with the
 * company name and decide which flow to show.
 */
export const useGetInvitation = (code: string | null) => {
  const { data, error, isLoading, isError } = useQuery({
    queryKey: invitationKeys.byCode(code),
    queryFn: async (): Promise<TInvitation> => {
      const res = await axios.get(`/invitations/${code}`);
      return res.data;
    },
    enabled: !!code,
    retry: false,
  });

  return { invitation: data, error, isLoading, isError };
};

/**
 * Every invitation issued by a company, whatever its status.
 *
 * The API already auto-expires stale rows in its response, so a `pending`
 * invitation past its 72h window comes back as `expired` without any write.
 *
 * This endpoint existed on the server from the start and had no caller: the
 * collaborator list only ever showed accepted members, so an invited person
 * was invisible until they signed up. There was no way to tell "never
 * invited" from "invited and waiting" from "invited and expired".
 */
export const useGetCompanyInvitations = (companyId: number, enabled = true) => {
  const { data, ...res } = useQuery({
    queryKey: invitationKeys.companyList(companyId),
    queryFn: async (): Promise<TCompanyInvitation[]> => {
      const { data } = await axios.get(
        `/companies/${companyId}/employees/invitations`
      );
      return data?.invitations ?? [];
    },
    enabled: enabled && !!companyId,
  });

  return { invitations: data ?? [], ...res };
};

/**
 * Resend an invitation. The server renews `expires_at` and re-sends the email,
 * so this is the recovery path for the 72h expiry.
 *
 * NOTE: the route parameter is the invitation CODE, not the numeric id
 * (see CompanyController::resendInvitation, which looks up by
 * `invitation_code`). Passing the id silently 404s.
 */
export const useResendInvitation = (companyId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitationCode: string) => {
      const { data } = await axios.post(
        `/companies/${companyId}/employees/invitation/${invitationCode}/resend`
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: invitationKeys.companyList(companyId),
      });
      toast.success(
        t("collaborator.invitationResent", "Invitation renvoyée")
      );
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          t("collaborator.invitationResendError", "Échec de l'envoi")
      );
    },
  });
};

/** Revoke a pending invitation so its link stops working. */
export const useRevokeInvitation = (companyId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitationCode: string) => {
      const { data } = await axios.post(
        `/companies/${companyId}/employees/invitation/${invitationCode}/revoke`
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: invitationKeys.companyList(companyId),
      });
      toast.success(t("collaborator.invitationRevoked", "Invitation révoquée"));
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          t("collaborator.invitationRevokeError", "Échec de la révocation")
      );
    },
  });
};
